import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ClassEntityService } from '../../../../services/entity-services/classes-entity.service';
import { DeletionPolicyService } from '../../../../services/entity-services/deletion-policy.service';
import { ErrorHandlerService } from '../../../../decoration-base/error-handler.service';
import { PolicyDetailsResponse } from '../../../../interfaces/policy-details.interface';
import { ResourceDetailsResponse } from '../../../../interfaces/resource-details.interface';
import { ResourceCreatorService } from '../../../../services/resource-creator.service';
import { NotificationService } from '../../../../services/notification.service';
import { Router } from '@angular/router';
import { Log, IShortLogger } from '../../../../services/logger.service';

interface PolicyTemplate {
  Id: string;
  PolicyName: string;
  Type: string;
  Description?: string;
}

interface ResourceTemplate {
  resourceType: string;
  id: string;
  name: string;
}

interface ClassData {
  id?: string;
  name?: string;
  requisitionId?: string;
  trainingSchedule?: {
    title?: string;
    course?: {
      code?: string;
      name?: string;
      provider?: string;
      description?: string;
    } | null;
    resourceTemplateIds?: ResourceTemplate[] | null;
  } | null;
  status?: string;
  stage?: string;
  customerDetails?: any;
  otherRemarks?: string;
  createdBy?: string;
  createdOn?: string;
  updatedOn?: string;
  updatedBy?: string;
}

@Component({
  selector: 'app-resource-policy-linking',
  templateUrl: './resource-policy-linking.component.html',
  styleUrl: './resource-policy-linking.component.css'
})
export class ResourcePolicyLinkingComponent implements OnInit {
  private readonly logger: IShortLogger<ResourcePolicyLinkingComponent> = Log.create(ResourcePolicyLinkingComponent);

  classId: string | null = null;
  classData: ClassData | null = null;
  loading: boolean = false;
  savingPolicy: boolean = false; // Loading state for confirm action

  // Resource type flags
  hasVMResources: boolean = false;
  hasSubscriptionResources: boolean = false;

  // VM Policy properties
  vmPolicies: PolicyTemplate[] = [];
  selectedVMPolicy: string = '';
  vmQuotaLimit: number = 200;

  // Subscription Policy properties
  subscriptionPolicies: PolicyTemplate[] = [];
  selectedSubscriptionPolicy: string = '';
  subscriptionQuotaLimit: number = 200;
  selectedCurrency: string = 'INR'; // Default currency
  availableCurrencies: string[] = ['INR', 'USD'];

  // Modal properties
  showAddVMPolicyModal: boolean = false;
  showAddSubscriptionPolicyModal: boolean = false;
  showViewDetailsModal: boolean = false;
  selectedPolicyDetails: PolicyDetailsResponse | null = null;

  // Resource details modal properties
  showResourceDetailsModal: boolean = false;
  selectedResourceDetails: ResourceDetailsResponse | null = null;
  loadingResourceDetails: boolean = false;
  loadingDetails: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private classEntityService: ClassEntityService,
    private deletionPolicyService: DeletionPolicyService,
    private errorHandler: ErrorHandlerService,
    private router: Router,
    private resourceCreatorService: ResourceCreatorService,
    private notification: NotificationService
  ) { }

  ngOnInit(): void {
    // Get classId from query parameters
    this.route.queryParams.subscribe(params => {
      this.classId = params['classId'] || null;
      if (this.classId) {
        this.logger.i('Resource Policy Linking for Class ID:', this.classId);
        this.loadClassDetails();
      }
    });
  }

  loadClassDetails(): void {
    if (!this.classId) return;

    this.loading = true;
    this.classEntityService.getClassByClassId(this.classId).subscribe({
      next: (response: any) => {
        this.logger.i('Raw API Response:', response);
        this.classData = response;
        this.logger.i('Class Data assigned:', this.classData);
        this.logger.i('Resource Template IDs:', this.classData?.trainingSchedule?.resourceTemplateIds);

        // Check if the response has the expected structure
        if (this.classData) {
          this.logger.i('Class Name:', this.classData.name);
          this.logger.i('Training Schedule:', this.classData.trainingSchedule);
          this.logger.i('Course:', this.classData.trainingSchedule?.course);
        }

        this.analyzeResourceTypes();
        this.loadPolicyTemplates();
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.errorHandler.handleError(error, 'Failed to load class details');
      }
    });
  }

  analyzeResourceTypes(): void {
    this.logger.i('Analyzing resource types...');
    if (!this.classData?.trainingSchedule?.resourceTemplateIds) {
      this.logger.i('No resourceTemplateIds found');
      return;
    }

    const resourceTemplateIds = this.classData.trainingSchedule.resourceTemplateIds;
    this.logger.i('resourceTemplateIds:', resourceTemplateIds);

    this.hasVMResources = resourceTemplateIds.some(
      (resource: any) => resource.resourceType && resource.resourceType.toLowerCase() === 'vm'
    );

    this.hasSubscriptionResources = resourceTemplateIds.some(
      (resource: any) => resource.resourceType && (resource.resourceType.toLowerCase() === 'acc' || resource.resourceType.toLowerCase() === 'subscription')
    );

    this.logger.i('Has VM Resources:', this.hasVMResources);
    this.logger.i('Has Subscription Resources:', this.hasSubscriptionResources);
  }

  loadPolicyTemplates(): void {
    const resourceTemplateIds: any[] = this.classData?.trainingSchedule?.resourceTemplateIds ?? [];

    // Load VM policies if VM resources exist
    if (this.hasVMResources) {
      this.deletionPolicyService.getResourcePoliciesByType('VM').subscribe({
        next: (policies) => {
          this.vmPolicies = policies;
          this.logger.i('Loaded VM Policies:', policies);
          // Pre-select the already-saved policy so re-opening the screen shows the current value.
          const vmResource = resourceTemplateIds.find(
            (r: any) => r.resourceType?.toLowerCase() === 'vm'
          );
          const existingId = vmResource?.policyTemplateId ?? vmResource?.PolicyTemplateId ?? '';
          if (existingId && policies.some((p: any) => p.Id === existingId)) {
            this.selectedVMPolicy = existingId;
            this.logger.i('Pre-selected VM Policy:', existingId);
          }
        },
        error: (error) => {
          this.logger.e('Error loading VM policies:', error);
        }
      });
    }

    // Load Subscription policies if subscription resources exist
    if (this.hasSubscriptionResources) {
      this.deletionPolicyService.getResourcePoliciesByType('Subscription').subscribe({
        next: (policies) => {
          this.subscriptionPolicies = policies;
          this.logger.i('Loaded Subscription Policies:', policies);
          // Pre-select the already-saved subscription policy.
          const accResource = resourceTemplateIds.find(
            (r: any) => ['acc', 'subscription'].includes(r.resourceType?.toLowerCase() ?? '')
          );
          const existingId = accResource?.policyTemplateId ?? accResource?.PolicyTemplateId ?? '';
          if (existingId && policies.some((p: any) => p.Id === existingId)) {
            this.selectedSubscriptionPolicy = existingId;
            this.logger.i('Pre-selected Subscription Policy:', existingId);
          }
        },
        error: (error) => {
          this.logger.e('Error loading subscription policies:', error);
        }
      });
    }
  }

  // Helper method to reload policies with promise
  loadPolicies(resourceType: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.deletionPolicyService.getResourcePoliciesByType(resourceType).subscribe({
        next: (policies) => {
          if (resourceType === 'VM') {
            this.vmPolicies = policies;
          } else if (resourceType === 'Subscription') {
            this.subscriptionPolicies = policies;
          }
          this.logger.i(`Loaded ${resourceType} Policies:`, policies);
          resolve();
        },
        error: (error) => {
          this.logger.e(`Error loading ${resourceType} policies:`, error);
          reject(error);
        }
      });
    });
  }

  getVMResources(): ResourceTemplate[] {
    if (!this.classData?.trainingSchedule?.resourceTemplateIds) return [];
    return this.classData.trainingSchedule.resourceTemplateIds.filter(
      (resource: any) => resource.resourceType.toLowerCase() === 'vm'
    );
  }

  getSubscriptionResources(): ResourceTemplate[] {
    if (!this.classData?.trainingSchedule?.resourceTemplateIds) return [];
    return this.classData.trainingSchedule.resourceTemplateIds.filter(
      (resource: any) => resource.resourceType.toLowerCase() === 'acc' || resource.resourceType.toLowerCase() === 'subscription'
    );
  }

  isFormValid(): boolean {
    // Check if VM section is valid (if VM resources exist)
    const vmValid = !this.hasVMResources || (this.hasVMResources && !!this.selectedVMPolicy);

    // Check if Subscription section is valid (if subscription resources exist)
    const subscriptionValid = !this.hasSubscriptionResources ||
      (this.hasSubscriptionResources && !!this.selectedSubscriptionPolicy && this.subscriptionQuotaLimit > 0);

    return vmValid && subscriptionValid;
  }

  onConfirm(): void {
    if (!this.isFormValid()) {
      this.logger.e('Form is not valid');
      return;
    }

    this.savingPolicy = true;

    // Build the request according to API format
    const resourceTemplateIds: any[] = [];

    // Add VM resources with their policy template IDs
    if (this.hasVMResources && this.selectedVMPolicy) {
      const vmResources = this.getVMResources();
      vmResources.forEach(vm => {
        resourceTemplateIds.push({
          ResourceType: vm.resourceType,
          Id: vm.id,
          Name: vm.name,
          PolicyTemplateId: this.selectedVMPolicy
        });
      });
    }

    // Add Subscription resources with their policy template IDs and quota information
    if (this.hasSubscriptionResources && this.selectedSubscriptionPolicy) {
      const subscriptionResources = this.getSubscriptionResources();
      subscriptionResources.forEach(sub => {
        resourceTemplateIds.push({
          ResourceType: sub.resourceType,
          Id: sub.id,
          Name: sub.name,
          PolicyTemplateId: this.selectedSubscriptionPolicy,
          QuotaCurrency: this.selectedCurrency,
          QuotaLimit: this.subscriptionQuotaLimit.toString()
        });
      });
    }

    const request = {
      ClassId: this.classId,
      "TrainingSchedule.ResourceTemplateIds": resourceTemplateIds
    };

    this.logger.i('API Request:', request);

    // Call the API
    this.deletionPolicyService.setQuotaLimitAndResourcePolicy(request).subscribe({
      next: (response) => {
        this.logger.i('Policy configuration saved successfully:', response);
        this.savingPolicy = false;
        // In the constructor, add private router: Router
        // private router: Router

        this.router.navigate(['/dashboard']);
        this.notification.notify('Resource policy linking configured successfully!',"success",3000);
        // Optionally navigate back or refresh data
      },
      error: (error) => {
        this.logger.e('Error saving policy configuration:', error);
        this.savingPolicy = false;
        this.errorHandler.handleError(error, 'Failed to save policy configuration');
      }
    });
  }

  // Modal methods for VM policy
  openAddVMPolicyModal(): void {
    this.showAddVMPolicyModal = true;
  }

  closeAddVMPolicyModal(): void {
    this.showAddVMPolicyModal = false;
  }

  onVMPolicyCreated(): void {
    this.closeAddVMPolicyModal();
    this.loadPolicies('VM').then(() => {
      // Auto-select the latest created policy (last in the array)
      if (this.vmPolicies.length > 0) {
        this.selectedVMPolicy = this.vmPolicies[this.vmPolicies.length - 1].Id;
      }
    });
  }

  // Modal methods for Subscription policy
  openAddSubscriptionPolicyModal(): void {
    this.showAddSubscriptionPolicyModal = true;
  }

  closeAddSubscriptionPolicyModal(): void {
    this.showAddSubscriptionPolicyModal = false;
  }

  onSubscriptionPolicyCreated(): void {
    this.closeAddSubscriptionPolicyModal();
    this.loadPolicies('Subscription').then(() => {
      // Auto-select the latest created policy (last in the array)
      if (this.subscriptionPolicies.length > 0) {
        this.selectedSubscriptionPolicy = this.subscriptionPolicies[this.subscriptionPolicies.length - 1].Id;
      }
    });
  }

  // View policy details methods
  viewVMPolicyDetails(): void {
    if (!this.selectedVMPolicy) return;

    const selectedPolicy = this.vmPolicies.find(p => p.Id === this.selectedVMPolicy);
    if (selectedPolicy) {
      this.viewPolicyDetails(selectedPolicy);
    }
  }

  viewSubscriptionPolicyDetails(): void {
    if (!this.selectedSubscriptionPolicy) return;

    const selectedPolicy = this.subscriptionPolicies.find(p => p.Id === this.selectedSubscriptionPolicy);
    if (selectedPolicy) {
      this.viewPolicyDetails(selectedPolicy);
    }
  }

  viewPolicyDetails(policy: PolicyTemplate): void {
    this.logger.i('View details:', policy);
    this.loadingDetails = true;
    this.selectedPolicyDetails = null;
    this.showViewDetailsModal = true;

    // Fetch detailed policy data
    this.deletionPolicyService.getResourcePolicyById(policy.Id).subscribe({
      next: (policyDetails: PolicyDetailsResponse) => {
        this.selectedPolicyDetails = policyDetails;
        this.loadingDetails = false;
        this.logger.i('Policy details loaded:', policyDetails);
      },
      error: (error) => {
        this.logger.e('Error loading policy details:', error);
        this.loadingDetails = false;
        this.showViewDetailsModal = false;
        this.errorHandler.handleError(error, 'Failed to load policy details');
      }
    });
  }

  closeViewDetailsModal(): void {
    this.showViewDetailsModal = false;
    this.selectedPolicyDetails = null;
    this.loadingDetails = false;
  }

  // Resource details modal methods
  viewResourceDetails(resourceTemplate: ResourceTemplate): void {
    this.logger.i('View resource details:', resourceTemplate);
    this.loadingResourceDetails = true;
    this.selectedResourceDetails = null;
    this.showResourceDetailsModal = true;

    // Fetch detailed resource data
    this.resourceCreatorService.getResource(resourceTemplate.id).subscribe({
      next: (resourceDetails: ResourceDetailsResponse) => {
        this.selectedResourceDetails = resourceDetails;
        this.loadingResourceDetails = false;
        this.logger.i('Resource details loaded:', resourceDetails);
      },
      error: (error) => {
        this.logger.e('Error loading resource details:', error);
        this.loadingResourceDetails = false;
        this.showResourceDetailsModal = false;
        this.errorHandler.handleError(error, 'Failed to load resource details');
      }
    });
  }

  closeResourceDetailsModal(): void {
    this.showResourceDetailsModal = false;
    this.selectedResourceDetails = null;
    this.loadingResourceDetails = false;
  }
}
